import { Chart, ChartConfiguration, ChartData } from 'chart.js';

import { monthYear } from '@src/Common/Helpers/dates';
interface Dataset {
    column: string;
    backgroundColor: string;
    data?: number[] | boolean[];
}

interface ChartDataset {
    label?: string;
    backgroundColor?: string;
    data?: number[] | boolean[];
    stack?: string;
    yAxisID?: string;
}

interface AxisZoomLimits {
    min: number | 'original';
    max: number | 'original';
    minRange?: number;
}

interface DisplaySettings {
    stacked: boolean;
    yMin: number | string | null;
    yMax: number | string | null;
}

const normalizeChartValue = (value: unknown): number | null => {
    if (value === null || typeof value === 'undefined' || value === '') {
        return null;
    }

    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }

    const normalized = Number(value);

    return Number.isFinite(normalized) ? normalized : null;
};

class ReportData {
    constructor() {}

    /**
     * The type of chart to display
     * @var string
     * @default 'bar'
     * @example 'bar', 'line', 'pie', 'scatter', 'table'
     */
    public type: string = 'bar';

    public labels: string[] = [];

    public axis: { x: string | null; y: Dataset[] } = {
        x: null,
        y: [],
    };

    public datasets: ChartDataset[] = [];

    public category: string | null = null;

    public categories: Dataset[] | string[] | null = null;

    public formatting: string | null = null;

    public displaySettings: DisplaySettings = {
        stacked: true,
        yMin: null,
        yMax: null,
    };

    public raw: object[] = [];

    public zoomStateCallback: ((chart: Chart) => void) | null = null;

    public hasMultipleCategories(): boolean {
        return this.categories && this.categories.length > 1;
    }

    public hasMultipleDatasets(): boolean {
        return this.datasets && this.datasets.length > 1;
    }

    public hasMultipleLines(): boolean {
        return this.hasMultipleDatasets() && this.type === 'line';
    }

    public isPieTypeChart(): boolean {
        return ['pie', 'doughnut'].includes(this.type);
    }

    public isTableChart(): boolean {
        return ['table', 'grouped_table'].includes(this.type);
    }

    public setCategory(category: string | null) {
        this.category = category;
    }

    public setCategories(categories: Dataset[] | string[] | null) {
        this.categories = categories;
    }

    public setDatasets(datasets: ChartDataset[]) {
        this.datasets = datasets;
    }

    /**
     * Set dimension to be displayed on axis
     * @param value string The dimension to be displayed on axis
     * @param axis string The axis to display the dimension on (x or y)
     */
    public setAxis(value, axis: 'y' | 'x') {
        this.axis[axis] = value;
    }

    public setFormatting(formatting: string | null) {
        this.formatting = formatting;
    }

    public setDisplaySettings(settings: Partial<DisplaySettings> | null = null) {
        this.displaySettings = {
            ...this.displaySettings,
            ...(settings ?? {}),
        };
    }

    public setZoomStateCallback(callback: ((chart: Chart) => void) | null) {
        this.zoomStateCallback = callback;
    }

    /**
     * Set the type of chart to display
     * @param type string The type of chart to display
     * @example 'bar', 'line', 'pie', 'scatter'
     */
    public setType(type: string) {
        this.type = type;
    }

    public setRawData(data) {
        this.raw = data;

        if (this.isTableChart()) {
            return data;
        }

        let result = [];
        if (this.isPieTypeChart()) {
            const dimension = this.axis.y.find(() => true);
            result = [
                {
                    label: dimension ? dimension.column : '',
                    data: dimension ? data.map((row) => row[this.axis.y.find(() => true).column]) : [],
                    backgroundColor: data.map((row) => {
                        const category = this.categories.find((category) => category.column == row[this.category]);

                        return category ? category.color : '#aaa';
                    }),
                    hoverOffset: 4,
                },
            ];
        } else if (!this.hasMultipleCategories() && this.axis.y) {
            result = this.axis.y.map((dataset) => {
                return {
                    label: dataset.column,
                    backgroundColor: dataset.backgroundColor,
                    data: data.map((row) => normalizeChartValue(row[dataset.column])),
                    borderColor: dataset.backgroundColor,
                    pointStyle: false,
                    borderWidth: 2,
                    tension: 0.2,
                    yAxisID: dataset.boolean ? 'boolean' : 'y',
                };
            });
        } else {
            if (!this.categories) {
                this.categories = [];
            }

            this.categories.forEach((category) => {
                this.axis.y.forEach((dataset, index) => {
                    result.push({
                        label: `${category.column} - ${dataset.column}`,
                        data: this.axis.x
                            ? this.getValuesFromAxis('x').map((xValue) => {
                                  const row = data.find(
                                      (row) => row[this.axis.x] === xValue && row[this.category] === category.column,
                                  );
                                  const yValue =
                                      row && this.axis.y && this.axis.y[index]
                                          ? normalizeChartValue(row[this.axis.y[index].column])
                                          : null;

                                  return {
                                      x: xValue,
                                      y: yValue,
                                  };
                              })
                            : [],
                        backgroundColor: category.color,
                        borderColor: category.color,
                        pointStyle: false,
                        borderWidth: 2,
                        tension: 0.2,
                        stack: dataset.column,
                        datalabels: {
                            formatter: (value, ctx) => {
                                // Group values by stack
                                const stacks = ctx.chart.data.datasets.reduce((acc, dataset, currentIndex) => {
                                    if (!acc[dataset.stack]) {
                                        acc[dataset.stack] = [];
                                    }

                                    acc[dataset.stack].push({ data: dataset.data[ctx.dataIndex], index: currentIndex });

                                    return acc;
                                }, {});

                                // Get highest index of each stack
                                const tops = Object.values(stacks).map(
                                    (stack: { data: { y: number | string; x: number | string }; index: number }[]) =>
                                        Math.max(
                                            ...stack
                                                .filter((item) => parseInt(item.data.y, 10) > 0)
                                                .map((item) => item.index),
                                            0,
                                        ),
                                );

                                if (!!value && tops.includes(ctx.datasetIndex)) {
                                    const stackTotal = stacks[ctx.dataset.stack]
                                        .filter((ds, i) => !ctx.chart.getDatasetMeta(i).hidden)
                                        .reduce((sum, v) => sum + (Number(v.data.y) || 0), 0);

                                    return `${ctx.dataset.stack}\n${stackTotal}`;
                                } else {
                                    return null;
                                }
                            },
                            color: 'rgba(38, 38, 38, 1)',
                            align: 'top',
                            anchor: 'end',
                            offset: 4,
                            textAlign: 'center',
                        },
                    });
                });
            });
        }

        this.setDatasets(result);

        return result;
    }

    public getValuesByProperty(supplied: object[], property: string | null = null) {
        return property ? [...new Set(supplied.map((row) => row[property]))] : [];
    }

    public getValuesFromAxis(axis: string) {
        return this.getValuesByProperty(this.raw, this.axis[axis]);
    }

    public setLabels(labels: string[]) {
        this.labels = labels;
    }

    public setLabelsFromData(property: string | null = null) {
        this.labels = this.getValuesByProperty(this.raw, this.axis.x || property);
    }

    public isTimeAxis(axis: string, timeStrings: string[] = ['date', 'datetime', 'created_at']) {
        return this.axis && this.axis[axis] && timeStrings.includes(this.axis[axis]);
    }

    public isBooleanAxis(axis: 'x' | 'y') {
        return this.axis && this.axis[axis] && this.axis[axis].filter((single) => single && single.boolean).length > 0;
    }

    public changeTimeUnit({ chart }) {
        const zoomLevel = chart.getZoomLevel();
        if (zoomLevel > 9) {
            chart.options.scales.x.time.unit = 'minute';
        } else if (chart.options.scales.x.time.unit === 'minute') {
            chart.options.scales.x.time.unit = 'hour';
        }
    }

    public handleZoomComplete(context) {
        if (this.isTimeAxis('x')) {
            this.changeTimeUnit(context);
        }

        this.zoomStateCallback?.(context.chart);
    }

    private getPointValue(point: unknown): number | null {
        if (point !== null && typeof point === 'object') {
            return normalizeChartValue((point as { y?: unknown }).y);
        }

        return normalizeChartValue(point);
    }

    private getStackedYExtent(datasets: ChartDataset[]): { min: number; max: number } {
        const sums: Record<string, Record<number, { positive: number; negative: number }>> = {};

        datasets.forEach((dataset) => {
            const stack = dataset.stack ?? 'default';
            sums[stack] = sums[stack] ?? {};

            (dataset.data as unknown[]).forEach((point, index) => {
                const value = this.getPointValue(point);
                if (value === null) {
                    return;
                }

                const sum = (sums[stack][index] = sums[stack][index] ?? { positive: 0, negative: 0 });
                if (value >= 0) {
                    sum.positive += value;
                } else {
                    sum.negative += value;
                }
            });
        });

        let min = Infinity;
        let max = -Infinity;
        Object.values(sums).forEach((byIndex) => {
            Object.values(byIndex).forEach(({ positive, negative }) => {
                min = Math.min(min, negative);
                max = Math.max(max, positive);
            });
        });

        return { min, max };
    }

    private getYExtent(): { min: number; max: number } | null {
        const datasets = this.datasets.filter(
            (dataset) => dataset.yAxisID !== 'boolean' && Array.isArray(dataset.data),
        );
        if (!datasets.length) {
            return null;
        }

        let min = Infinity;
        let max = -Infinity;

        if (this.displaySettings.stacked) {
            ({ min, max } = this.getStackedYExtent(datasets));
        } else {
            datasets.forEach((dataset) => {
                (dataset.data as unknown[]).forEach((point) => {
                    const value = this.getPointValue(point);
                    if (value !== null) {
                        min = Math.min(min, value);
                        max = Math.max(max, value);
                    }
                });
            });
        }

        return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : null;
    }

    private getYZoomLimits(): AxisZoomLimits {
        if (this.isBooleanAxis('y')) {
            return { min: 'original', max: 'original' };
        }

        const yMin = normalizeChartValue(this.displaySettings.yMin);
        const yMax = normalizeChartValue(this.displaySettings.yMax);
        const extent = this.getYExtent();

        if (!extent && yMin === null && yMax === null) {
            return { min: 'original', max: 'original' };
        }

        // Without an explicit yMin the scale begins at zero, so the limit must not open negative space.
        const dataMin = extent ? Math.min(0, extent.min) : 0;
        const dataMax = extent ? extent.max : 0;
        const headroom = (dataMax - dataMin) * 0.05 || 1;

        const min = yMin ?? (dataMin < 0 ? dataMin - headroom : dataMin);
        const max = yMax ?? dataMax + headroom;

        return { min, max, minRange: (max - min) * 0.05 };
    }

    private parseTimeValue(value: unknown): number | null {
        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : null;
        }

        if (typeof value === 'string' && value !== '') {
            const timestamp = Date.parse(`${value.replace(' ', 'T')}Z`);

            return Number.isNaN(timestamp) ? null : timestamp;
        }

        return null;
    }

    private getXZoomLimits(): AxisZoomLimits {
        if (this.isTimeAxis('x')) {
            const minuteInMs = 60000;
            const timestamps = this.labels
                .map((label) => this.parseTimeValue(label))
                .filter((value): value is number => value !== null);

            if (!timestamps.length) {
                return { min: 'original', max: 'original', minRange: minuteInMs };
            }

            return { min: Math.min(...timestamps), max: Math.max(...timestamps), minRange: minuteInMs };
        }

        if (!this.labels.length) {
            return { min: 'original', max: 'original', minRange: 2 };
        }

        // Category scale limits are label indexes; keep at least three points in view.
        return { min: 0, max: this.labels.length - 1, minRange: 2 };
    }

    public getChartJsOptions(settings: object = {}) {
        const timeScaleOptions = {
            x: {
                type: 'time',
                ticks: {
                    autoSkip: true,
                    autoSkipPadding: 20,
                    maxRotation: 0,
                },
                adapters: {
                    date: {
                        locale: 'CS-CZ',
                        zone: 'Europe/Prague',
                    },
                },
                time: {
                    parser: function (value) {
                        if (typeof value === 'number') {
                            return value;
                        }
                        const result = value.replace(' ', 'T');
                        return `${result}Z`;
                    },
                    unit: 'hour',
                    tooltipFormat: 'DD T',
                    displayFormats: {
                        hour: 'dd.LL t',
                        minute: 'dd.LL t',
                    },
                },
            },
        };

        const defaults = {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
                intersect: false,
            },
            scales: {
                x: {},
                y: {},
            },
            plugins: {
                legend: {
                    position: 'bottom',
                    labels: {
                        padding: 20,
                    },
                    onClick: () => {
                        return;
                    },
                },
                zoom: {
                    limits: {
                        x: this.getXZoomLimits(),
                        y: this.getYZoomLimits(),
                    },
                    drawTime: 'beforeDraw',
                    pan: {
                        enabled: true,
                        mode: 'xy',
                        onPanComplete: ({ chart }) => this.zoomStateCallback?.(chart),
                    },
                    zoom: {
                        wheel: {
                            enabled: true,
                            speed: 0.2,
                        },
                        pinch: {
                            enabled: true,
                        },
                        mode: 'xy',
                        onZoomComplete: (context) => this.handleZoomComplete(context),
                    },
                },
            },
        };

        settings = Object.assign(defaults, settings);

        if (this.type === 'bar') {
            settings.plugins.tooltip = {
                mode: 'x',
            };
        }

        if (['line', 'bar'].includes(this.type)) {
            settings.scales.x = {
                ...settings.scales?.x,
                stacked: this.displaySettings.stacked,
            };

            settings.scales.y = {
                ...settings.scales?.y,
                stacked: this.displaySettings.stacked,
            };

            if (this.displaySettings.yMin === null || this.displaySettings.yMin === '') {
                settings.scales.y.beginAtZero = true;
            }

            if (this.displaySettings.yMin !== null && this.displaySettings.yMin !== '') {
                settings.scales.y.min = Number(this.displaySettings.yMin);
            }

            if (this.displaySettings.yMax !== null && this.displaySettings.yMax !== '') {
                settings.scales.y.max = Number(this.displaySettings.yMax);
            }
        }

        if (this.category) {
            settings.scales.x = {
                ...settings.scales.x,
                stacked: this.displaySettings.stacked,
                ticks: {
                    maxRotation: 90,
                    minRotation: 45,
                },
            };

            settings.scales.y = {
                ...settings.scales.y,
                stacked: this.displaySettings.stacked,
            };
        }

        if (this.formatting) {
            settings.scales.x.ticks = {
                ...settings.scales.x.ticks,
                callback: (value, index) => {
                    switch (this.formatting) {
                        case 'formatMonthYear':
                            return monthYear(...this.labels[index].split('-'));
                    }

                    return `${this.labels[index]}`;
                },
            };
        }

        if (this.isTimeAxis('x')) {
            settings.scales.x = timeScaleOptions.x;
        }

        if (this.isBooleanAxis('y')) {
            settings.scales.boolean = {
                type: 'category',
                position: 'left',
                labels: [1, 0],
                offset: true,
                stack: true,
                stackWeight: 1,
            };
        }

        if (this.isPieTypeChart()) {
            delete settings['scales'];
        }

        return settings;
    }

    public getChartJsData(): ChartData {
        return {
            labels: this.labels,
            datasets: this.datasets,
        };
    }

    public getChartJs(): ChartConfiguration {
        return {
            type: this.type,
            data: this.getChartJsData(),
            options: this.getChartJsOptions(),
        };
    }
}

export { ReportData };
